/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { VideoPlayerStates, PlaybackErrorType, PlayerLoadModeStates } from '../enums';
import { AppUtils } from '../../AppUtils';
import {
    BUFFER_THRESHOLD_OFFSET,
    CALIBRATION_VALUE,
    DEFAULT_SEEK_RANGE_IN_MILLISECONDS,
    MILLISECONDS_IN_A_SECOND,
    SPEED,
} from '../../../config';
import { Constants } from '../../constants';
import { Storage } from '../../storage/Storage';
import shakaConfig from './ShakaPlayerConfig';
import { ShakaPlayerErrorMapper } from './ShakaPlayerErrorMapper';
import { QoEAppLogger, VuLogger, Log } from '../../logging';
import { PlaybackError } from '../../error/PlaybackError';
import { NetworkManager } from '../../network/NetworkManager';
import { DeviceModel } from '../../device/DeviceModel';
import { IPlayerStatus, IVideoPlayer } from '../interfaces';
import { LiveTvPlaybackError } from '../error';

// TODO: To be used in release build. [KST-2698]
const shaka = require('shaka-player/shaka-player.compiled');
// const shaka = require('shaka-player/shaka-player.compiled.debug');

shaka.polyfill.installAll();

export class ShakaPlayerImpl implements IVideoPlayer {
    private static oIVideoPlayer: IVideoPlayer;

    private static TAG = 'ShakaPlayerImpl';

    private static RETRY_LIMIT = 5;

    private videoUrl: string = '';

    // if cc true, it will enable cc/subtitle
    private cc: boolean = false;

    private subtitleTracks: any[] = [];

    private mAudioTracks: any[] = [];

    private retryCount = 0;

    private errorTime: any = null;

    private mMediaElement: HTMLMediaElement | null = null;

    private playbackRetryTimerId: any = null;

    public liveCacheDuration = DEFAULT_SEEK_RANGE_IN_MILLISECONDS;

    // currently shakaPlayer don't have listener to detect firstFrame event
    // this flag is required to log firstframe event.
    public isFirstFrameTriggered = false;

    mVideoPlayer: any;

    private trickPlayIntervalId: any = null;

    private playErrorIntervalId: any = null;

    private trickPlayTime: number = SPEED.SPEED_FASTFORWARD_1;

    private isTrickPlayEnable = false;

    private mPlayerStatus: IPlayerStatus;

    private static mVideoPlayerStateChangeCallback: Function = () => null;

    private constructor() {
        this.mPlayerStatus = { state: VideoPlayerStates.NONE };
        this.mVideoPlayer = new shaka.Player();
        // TODO: To be used in release build. [KST-2698]
        // shaka.log.setLevel(shaka.log.Level.V2);
        this.setUpPlayer();
    }

    static getInstance(): IVideoPlayer {
        if (!ShakaPlayerImpl.oIVideoPlayer) {
            ShakaPlayerImpl.oIVideoPlayer = new ShakaPlayerImpl();
        }
        return ShakaPlayerImpl.oIVideoPlayer;
    }

    // eslint-disable-next-line class-methods-use-this
    private onVideoPlayerStateChangeHandler(): void {
        ShakaPlayerImpl.mVideoPlayerStateChangeCallback(this.mPlayerStatus);
    }

    private setVideoPlayerState(status: IPlayerStatus): void {
        this.mPlayerStatus = status;
        this.onVideoPlayerStateChangeHandler();
    }

    // eslint-disable-next-line class-methods-use-this
    public addVideoPlayerStateChangeListener(callback: Function): void {
        ShakaPlayerImpl.mVideoPlayerStateChangeCallback = callback;
    }

    configure(config: any): void {
        this.mMediaElement = config.playerEl;
        this.mVideoPlayer.attach(config.playerEl);
        this.mVideoPlayer.setVideoContainer(config.playerRootEl);
        this.registerEventListeners();
        this.cc = Storage.getInstance(Storage.StorageType.SESSION_STORAGE).getItemWithDefaultValue(
            Storage.Keys.PLAYER_CC_STATUS,
            false,
        );
    }

    setAudioTrackByLanguage(language: string): void {
        Diagnostics.info(ShakaPlayerImpl.TAG, `setAudioTrackByLanguage {language: `, language, '}');
        Log.i(ShakaPlayerImpl.TAG, `setAudioTrackByLanguage {language: `, language, '}');
        this.mVideoPlayer.selectAudioLanguage(language);
    }

    setAudioTrackByID(audioID: number): number {
        if (this.getCurrentAudioTrack()) {
            if (audioID === this.getCurrentAudioTrack().audioId) {
                Diagnostics.info(ShakaPlayerImpl.TAG, `Already playing {audioId: `, audioID, '}');
                Log.i(ShakaPlayerImpl.TAG, `Already playing {audioId: `, audioID, '}');
                return -1;
            }
        }
        if (this.mAudioTracks.length > 0) {
            const track = this.mAudioTracks.find(({ audioId }) => audioId === audioID);
            if (track) {
                this.mVideoPlayer.selectVariantTrack(track, true /* clearBuffer */);
                QoEAppLogger.logAudioTrackChanged();
                QoEAppLogger.logStreamingStartEvent();
                return track.id;
            }
            Diagnostics.error(ShakaPlayerImpl.TAG, 'Audio Track Error');
            Log.e(ShakaPlayerImpl.TAG, 'Audio Track Error');
        }
        return -1;
    }

    private getAudioLanguages(): string[] {
        return this.mVideoPlayer.getAudioLanguages();
    }

    private getVariants(): any[] {
        return this.mVideoPlayer.getVariantTracks();
    }

    getVideoVarients() {
        return this.getVariants();
    }

    /**
     * to fatch and filter the subtitle type SMPTE
     */
    public fetchTextTrack() {
        const mimeTypeMP4 = 'application/mp4';
        const ttmlCodecs = 'stpp';
        // if we need to support more subtitle type, we need to add it to below array.
        const supportedMimeType = ['application/ttml+xml', mimeTypeMP4];

        this.subtitleTracks = this.mVideoPlayer
            .getTextTracks()
            .filter((track: { mimeType: string; codecs: string }) => {
                return supportedMimeType.some((type) => {
                    if (type === mimeTypeMP4) {
                        return type === track.mimeType && track.codecs.includes(ttmlCodecs);
                    }
                    return type === track.mimeType;
                });
            });
    }

    public getSeekRangeStartTimeInMs(): number {
        return this.mVideoPlayer.seekRange().start * MILLISECONDS_IN_A_SECOND;
    }

    public getSeekRangeEndTimeInMs(): number {
        return this.mVideoPlayer.seekRange().end * MILLISECONDS_IN_A_SECOND;
    }

    getCurrentAudioTrack(): any {
        return this.getVariants().find(({ active }) => active === true);
    }

    getCurrentTimeInMs(): number {
        if (this.mMediaElement) {
            return this.mMediaElement.currentTime * MILLISECONDS_IN_A_SECOND;
        }
        return Date.now();
    }

    isPlaybackStarted(): boolean {
        if (this.mVideoPlayer) {
            const time = this.getPlayerStats()?.playTime || 0;
            if (time) return true;
        }
        return false;
    }

    getSupportedAudioTracks(): any[] {
        if (this.mAudioTracks.length === 0) {
            const variants = this.getVariants();
            /* find unique combination of Language and audioId
             * from the variants to fetch multiple tracks of
             * same Language
             */
            Diagnostics.debug(ShakaPlayerImpl.TAG, `{variants: `, variants, '}');
            Log.d(ShakaPlayerImpl.TAG, `{variants: `, variants, '}');
            this.getAudioLanguages().forEach((lang: string) => {
                const audioIds: number[] = [];
                variants.forEach((variant: any) => {
                    if (audioIds.includes(variant.audioId) === false && variant.language === lang) {
                        audioIds.push(variant.audioId);
                        this.mAudioTracks.push(variant);
                    }
                });
            });
        }
        Diagnostics.debug(
            ShakaPlayerImpl.TAG,
            `Found Supported Tracks {mAudioTracks: `,
            this.mAudioTracks,
            '}',
        );
        Log.d(
            ShakaPlayerImpl.TAG,
            `Found Supported Tracks {mAudioTracks: `,
            this.mAudioTracks,
            '}',
        );
        return this.mAudioTracks;
    }

    isAudioTrackChangeSupported(): boolean {
        return this.getAudioLanguages().length > 1;
    }

    isTextTrackSupported(): boolean {
        return this.subtitleTracks.length > 0;
    }

    isTextTrackChangeSupported(): boolean {
        return this.subtitleTracks.length > 1;
    }

    getSupportedSubtitles(): any[] {
        return this.subtitleTracks;
    }

    setSubtitlesByLanguage(sLanguage?: string): number {
        if (this.subtitleTracks.length === 0) {
            Diagnostics.debug(ShakaPlayerImpl.TAG, `No supported Subtitle available`);
            Log.d(ShakaPlayerImpl.TAG, `No supported Subtitle available`);
            return -1;
        }
        this.displaySubtitles(true);
        const selectedTrack = this.subtitleTracks.find(({ language }) => language === sLanguage);
        const track = selectedTrack || this.getDefaultTextTrack();
        if (track) {
            this.mVideoPlayer.selectTextTrack(track);
            return track.id;
        }
        return -1;
    }

    private getDefaultTextTrack(): any {
        const defaultTextTrack = this.subtitleTracks.find(({ primary }) => primary === true);
        return defaultTextTrack || this.subtitleTracks[0];
    }

    public pause(): void {
        if (this.mMediaElement) {
            this.mMediaElement.pause();
            Diagnostics.info(
                ShakaPlayerImpl.TAG,
                'Pausing Video at {mMediaElement.currentTime: ',
                this.mMediaElement.currentTime,
                '}',
            );
            Log.i(
                ShakaPlayerImpl.TAG,
                'Pausing Video at {mMediaElement.currentTime: ',
                this.mMediaElement.currentTime,
                '}',
            );
        }
    }

    public unPause(): void {
        if (this.mMediaElement) {
            this.mMediaElement.play();
            Diagnostics.info(
                ShakaPlayerImpl.TAG,
                'Playing Video at {mMediaElement.currentTime: ',
                this.mMediaElement.currentTime,
                '}',
            );
            Log.i(
                ShakaPlayerImpl.TAG,
                'Playing Video at {mMediaElement.currentTime: ',
                this.mMediaElement.currentTime,
                '}',
            );
        }
    }

    public mute(): void {
        if (this.mMediaElement) {
            this.mMediaElement.muted = true;
            Diagnostics.info(ShakaPlayerImpl.TAG, 'Muted Audio');
            Log.i(ShakaPlayerImpl.TAG, 'Muted Audio');
        }
    }

    public unMute(): void {
        if (this.mMediaElement) {
            this.mMediaElement.muted = false;
            Diagnostics.info(ShakaPlayerImpl.TAG, 'Unmuted Audio');
            Log.i(ShakaPlayerImpl.TAG, 'Unmuted Audio');
        }
    }

    public seekTo(position: number): void {
        if (this.mMediaElement && this.mVideoPlayer) {
            const newTime = this.mMediaElement.currentTime + position;
            if (
                newTime >= this.mVideoPlayer.seekRange().start &&
                newTime <= this.mVideoPlayer.seekRange().end
            )
                this.mMediaElement.currentTime = newTime;
            else {
                Diagnostics.info(
                    ShakaPlayerImpl.TAG,
                    'Requested position is crossing start and end seek range.',
                );
                Log.i(
                    ShakaPlayerImpl.TAG,
                    'Requested position is crossing start and end seek range.',
                );
                if (position > 0)
                    this.mMediaElement.currentTime = Math.floor(this.mVideoPlayer.seekRange().end);
                else {
                    this.mMediaElement.currentTime = Math.floor(
                        this.mVideoPlayer.seekRange().start,
                    );
                }
            }
            Diagnostics.info(
                ShakaPlayerImpl.TAG,
                'Seeking at {mMediaElement.currentTime: ',
                this.mMediaElement.currentTime,
                '}',
            );
            Log.i(
                ShakaPlayerImpl.TAG,
                'Seeking at {mMediaElement.currentTime: ',
                this.mMediaElement.currentTime,
                '}',
            );
        }
    }

    public getLiveCacheDuration() {
        return this.liveCacheDuration;
    }

    public getSeekRangeInMs(): number {
        const { start, end } = this.mVideoPlayer.seekRange();
        return Math.round(end - start) * MILLISECONDS_IN_A_SECOND;
    }

    public goToLive(): void {
        if (this.mMediaElement) {
            this.mMediaElement.currentTime = Math.floor(this.mVideoPlayer.seekRange().end);
        }
    }

    public startFromBuffer(): void {
        if (this.mMediaElement) {
            this.mMediaElement.currentTime = Math.floor(this.mVideoPlayer.seekRange().start);
        }
    }

    public getTrickPlayTimeInSeconds(): number {
        return this.trickPlayTime;
    }

    public setTrickPlayTimeInSeconds(time: number): void {
        this.trickPlayTime = time;
    }

    public setSpeed(rate: number): void {
        if (this.mVideoPlayer && this.mMediaElement && this.isPlaybackStarted()) {
            if (rate < 0) {
                if (this.isBufferThresholdReachedEnd()) {
                    this.mMediaElement.currentTime -= BUFFER_THRESHOLD_OFFSET + CALIBRATION_VALUE;
                }
            }
            if (rate > 1) {
                if (this.isBufferThresholdReachedStart()) {
                    this.mMediaElement.currentTime += BUFFER_THRESHOLD_OFFSET + CALIBRATION_VALUE;
                }
            }

            if (rate === SPEED.SPEED_NORMAL) {
                this.isTrickPlayEnable = false;
                this.trickPlayTime = SPEED.SPEED_FASTFORWARD_1;
                this.unPause();
                clearInterval(this.trickPlayIntervalId);
                this.trickPlayIntervalId = null;
            } else {
                this.isTrickPlayEnable = true;
                if (this.trickPlayIntervalId) {
                    clearInterval(this.trickPlayIntervalId);
                    this.trickPlayIntervalId = null;
                }
                this.trickPlayIntervalId = setInterval(() => {
                    this.trickPlayTime += Math.abs(rate);
                    this.seekTo(rate);
                    this.pause();
                }, MILLISECONDS_IN_A_SECOND);
            }
        }
    }

    public isTrickPlayEnabled(): boolean {
        return this.isTrickPlayEnable;
    }

    public isBufferThresholdReachedStart(): boolean {
        if (this.mMediaElement && this.mVideoPlayer) {
            const startTime = this.mVideoPlayer.seekRange().start;
            if (this.isPlaybackStarted()) {
                if (this.mVideoPlayer.isLive()) {
                    if (
                        startTime &&
                        this.mMediaElement.currentTime - startTime < BUFFER_THRESHOLD_OFFSET
                    )
                        return true;
                }
                return startTime === this.mMediaElement.currentTime;
            }
        }
        return false;
    }

    public isBufferThresholdReachedEnd(): boolean {
        if (this.mMediaElement && this.mVideoPlayer) {
            const endTime = this.mVideoPlayer.seekRange().end;
            if (this.isPlaybackStarted()) {
                if (endTime && endTime - this.mMediaElement.currentTime < BUFFER_THRESHOLD_OFFSET)
                    return true;
            }
        }
        return false;
    }

    public isBufferThresholdReached(): boolean {
        if (this.isBufferThresholdReachedStart() || this.isBufferThresholdReachedEnd()) {
            return true;
        }
        return false;
    }

    public isEndOfStream(): boolean {
        if (this.mMediaElement?.ended) return true;
        if (!this.isLive() && this.mMediaElement && this.getSeekRangeEndTimeInMs() > 0) {
            const endTime = this.getSeekRangeEndTimeInMs() / MILLISECONDS_IN_A_SECOND;
            if (endTime - this.mMediaElement.currentTime < Constants.END_OF_THRESHOLD) return true;
        }
        return false;
    }

    public isLive(): boolean {
        return this.mVideoPlayer.isLive();
    }

    play(url: string, position?: number) {
        const videoUrl = url;
        this.setVideoPlayerState({ state: VideoPlayerStates.INITIATION });
        // setting false in case the user change channel or do a retune
        VuLogger.streamingRecoveryFlag = false;

        if (videoUrl) {
            QoEAppLogger.updateStreamingUrl(videoUrl);
            this.videoUrl = videoUrl;
            Diagnostics.debug(ShakaPlayerImpl.TAG, '{videoUrl: ', this.videoUrl, '}');
            Log.d(ShakaPlayerImpl.TAG, '{videoUrl: ', this.videoUrl, '}');
            this.setVideoPlayerState({ state: VideoPlayerStates.LOADING });
            this.retryCount = 0;
            this.errorTime = null;
            this.playVideoUrl(position);
        } else {
            Diagnostics.error(ShakaPlayerImpl.TAG, 'Video URL is not available');
            Log.e(ShakaPlayerImpl.TAG, 'Video URL is not available');
        }
    }

    public resetPlayerConfiguration() {
        this.mVideoPlayer.resetConfiguration();
        this.setUpPlayer();
    }

    public overrideAvailabilityWindowForLinear(): void {
        let availabilityWindow =
            this.mVideoPlayer.seekRange().end - this.mVideoPlayer.seekRange().start;
        const availabilityWindowFromConfig =
            this.mVideoPlayer.getConfiguration().manifest.availabilityWindowOverride;
        if (availabilityWindowFromConfig) {
            availabilityWindow = Math.min(availabilityWindow, availabilityWindowFromConfig);
        }
        this.mVideoPlayer.configure({
            manifest: {
                availabilityWindowOverride: availabilityWindow,
            },
        });
    }

    public overrideAvailabilityWindowForStartOver(): void {
        this.mVideoPlayer.configure({
            manifest: {
                availabilityWindowOverride: Infinity,
            },
        });
    }

    public setUpDRM(url: string, header: string, vuid: string, fullUrl?: string) {
        if (DeviceModel.inFrame()) {
            this.mVideoPlayer.configure({
                drm: {
                    servers: {
                        'com.widevine.alpha':
                            fullUrl || `${url}?${header}=${AppUtils.getBase64String(vuid)}`,
                    },
                },
            });
        } else {
            this.mVideoPlayer.configure({
                drm: {
                    servers: {
                        'com.widevine.alpha':
                            fullUrl || `${url}?${header}=${AppUtils.getBase64String(vuid)}`,
                    },
                    advanced: {
                        'com.widevine.alpha': {
                            persistentStateRequired: true,
                        },
                    },
                },
            });
        }
    }

    public setVideoUrl = (videoUrl: string) => {
        this.videoUrl = videoUrl;
        this.retryCount = 0;
        this.errorTime = null;
        this.playVideoUrl();
    };

    private setUpPlayer() {
        Log.i(ShakaPlayerImpl.TAG, 'setUpPlayer');
        if (this.mVideoPlayer && shakaConfig) {
            this.mVideoPlayer.configure(shakaConfig);
        }
    }

    private handleCaptionPreference(): void {
        if (this.isTextTrackSupported()) {
            this.displaySubtitles(this.cc);
            const selectedLanguage = Storage.getInstance(
                Storage.StorageType.SESSION_STORAGE,
            ).getItemWithDefaultValue(Storage.Keys.PLAYER_CC_LANGUAGE, '');
            if (this.cc) this.setSubtitlesByLanguage(selectedLanguage);
        }
    }

    private async handlePlaybackError(error: any) {
        const uiErrorCode = ShakaPlayerErrorMapper.mapUiError(error);
        const subErrorCode = ShakaPlayerErrorMapper.getSubErrorCode(error);
        const errorMessageType = ShakaPlayerErrorMapper.mapErrorMessageType(error.category);
        const errorCode = `${uiErrorCode}-${subErrorCode}`;
        Diagnostics.error(
            ShakaPlayerImpl.TAG,
            'handlePlaybackError {error: ',
            JSON.stringify(error),
            '}',
        );
        Log.e(ShakaPlayerImpl.TAG, 'handlePlaybackError {error: ', JSON.stringify(error), '}');
        // in case of network error, we check if video buffer is availabe the we play buffer
        // and once buffer is empty then we show error
        if (
            !NetworkManager.isNetworkDisconnected() &&
            error.category === shaka.util.Error.Category.NETWORK &&
            !this.isTrickPlayEnable &&
            this.isErrorBufferEnded(uiErrorCode, subErrorCode, error.code, error.category)
        ) {
            this.handleShakaNetworkError(uiErrorCode, subErrorCode, error.code, error.category);
        } else if (
            ShakaPlayerErrorMapper.isRetryableError(error) &&
            this.retryCount < ShakaPlayerImpl.RETRY_LIMIT
        ) {
            this.setVideoPlayerState({ state: VideoPlayerStates.ERROR_RECOVERY, code: errorCode });
            await this.unloadPlayer();
            Diagnostics.info(ShakaPlayerImpl.TAG, 'Retry attemp:', this.retryCount);
            Diagnostics.info(
                ShakaPlayerImpl.TAG,
                'handlePlaybackError {retryCount: ',
                this.retryCount,
                '}',
            );
            Log.i(ShakaPlayerImpl.TAG, 'handlePlaybackError {retryCount: ', this.retryCount, '}');
            // setting streamingRecoveryFlag true as player entered recovery
            VuLogger.streamingRecoveryFlag = true;
            this.playbackRetryTimerId = setTimeout(() => {
                this.retryCount += 1;
                this.playVideoUrl();
            }, Constants.PLAYER_RETRY_TIMEOUT);
        } else if (error.category !== shaka.util.Error.Category.NETWORK) {
            // setting false in case the error did not recover
            VuLogger.streamingRecoveryFlag = false;
            VuLogger.logErrorEvent({
                code: error.code,
                message: errorMessageType.message,
                source: VuLogger.ErrorEventSource.STREAMING_SESSION_ERROR,
                type: errorMessageType.type,
            });
            this.showPlayerError(uiErrorCode, subErrorCode, error.code);
            VuLogger.logNotifyEvent(
                VuLogger.NotifyEventSourceType.ERROR_OVERLAY,
                VuLogger.NotifyEventSourceName.PLAYER_ERROR,
            );
        }
    }

    private async handleShakaNetworkError(
        uiErrorCode: string,
        subErrorCode: string,
        errorCode,
        errorCategory: number,
    ) {
        if (this.retryCount < ShakaPlayerImpl.RETRY_LIMIT) {
            this.errorTime = null;
            this.setVideoPlayerState({
                state: VideoPlayerStates.ERROR_RECOVERY,
                code: `${uiErrorCode}-${subErrorCode}`,
            });
            await this.unloadPlayer();
            Diagnostics.info(
                ShakaPlayerImpl.TAG,
                'handleShakaNetworkError {retryCount: ',
                this.retryCount,
                '}',
            );
            Diagnostics.info(
                ShakaPlayerImpl.TAG,
                'handleShakaNetworkError {retryCount: ',
                this.retryCount,
                '}',
            );
            Log.i(
                ShakaPlayerImpl.TAG,
                'handleShakaNetworkError {retryCount: ',
                this.retryCount,
                '}',
            );
            this.playbackRetryTimerId = setTimeout(() => {
                this.retryCount += 1;
                this.playVideoUrl();
            }, Constants.PLAYER_RETRY_TIMEOUT);
        } else {
            // setting false in case the error did not recover
            VuLogger.streamingRecoveryFlag = false;
            const errorMessageType = ShakaPlayerErrorMapper.mapErrorMessageType(errorCategory);
            VuLogger.logErrorEvent({
                code: errorCode,
                message: errorMessageType.message,
                source: VuLogger.ErrorEventSource.STREAMING_SESSION_ERROR,
                type: errorMessageType.type,
            });
            this.showPlayerError(uiErrorCode, subErrorCode, errorCode);
            VuLogger.logNotifyEvent(
                VuLogger.NotifyEventSourceType.ERROR_OVERLAY,
                VuLogger.NotifyEventSourceName.PLAYER_ERROR,
            );
        }
    }

    /**
     * check if video play cache is played
     * @returns true if video buffer cache is played
     */
    private isErrorBufferEnded(
        uiErrorCode: string,
        subErrorCode: string,
        errorCode,
        errorCategory: number,
    ): boolean {
        const currentTime = Date.now();
        Diagnostics.info(
            ShakaPlayerImpl.TAG,
            'Checking if isErrorBufferEnded, {retryCount: ',
            this.retryCount,
            '} {isPlaybackStarted(): ',
            this.isPlaybackStarted(),
            '}',
        );
        Log.i(
            ShakaPlayerImpl.TAG,
            'Checking if isErrorBufferEnded, {retryCount: ',
            this.retryCount,
            '} {isPlaybackStarted(): ',
            this.isPlaybackStarted(),
            '}',
        );
        // setting streamingRecoveryFlag true as player entered recovery
        VuLogger.streamingRecoveryFlag = true;
        let isBufferEnded = true;
        if ((this.isPlaybackStarted() || this.retryCount > 0) && this.errorTime === null) {
            this.errorTime = AppUtils.addSecondsToDateTime(
                new Date(),
                this.retryCount > 0 ? Constants.THIRTY_SECONDS : Constants.FIFTY_SECONDS,
            );
            isBufferEnded = false;
        } else if (this.errorTime !== null && this.errorTime.getTime() > currentTime) {
            isBufferEnded = false;
        } else if (!this.isPlaybackStarted() && this.retryCount === 0) {
            this.retryCount = ShakaPlayerImpl.RETRY_LIMIT;
            isBufferEnded = true;
        }

        this.clearPlayerErrorTimer();
        Diagnostics.info(
            ShakaPlayerImpl.TAG,
            'Starting PlayerErrorTimer ',
            ' {retryCount: ',
            this.retryCount,
            '}',
        );
        Log.i(
            ShakaPlayerImpl.TAG,
            'Starting PlayerErrorTimer ',
            ' {retryCount: ',
            this.retryCount,
            '}',
        );
        this.playErrorIntervalId = setInterval(
            () => {
                Diagnostics.info(
                    ShakaPlayerImpl.TAG,
                    'Reload the player due to error timeout',
                    ' {retryCount: ',
                    this.retryCount,
                    '}',
                );
                Log.i(
                    ShakaPlayerImpl.TAG,
                    'Reload the player due to error timeout',
                    ' {retryCount: ',
                    this.retryCount,
                    '}',
                );
                this.handleShakaNetworkError(uiErrorCode, subErrorCode, errorCode, errorCategory);
            },
            this.retryCount > 0
                ? Constants.THIRTY_SECONDS_IN_MILLISECOND
                : Constants.FIFTY_SECONDS_IN_MILLISECOND,
        );
        return isBufferEnded;
    }

    private clearPlayerErrorTimer(): void {
        Diagnostics.info(
            ShakaPlayerImpl.TAG,
            'clearPlayerErrorTimer {retryCount:',
            this.retryCount,
            '}',
        );
        Log.i(ShakaPlayerImpl.TAG, 'clearPlayerErrorTimer {retryCount:', this.retryCount, '}');
        if (this.playErrorIntervalId) {
            clearInterval(this.playErrorIntervalId);
            this.playErrorIntervalId = null;
        }
    }

    public getPlayerStats = () => {
        return this.mVideoPlayer?.getStats() || {};
    };

    private showPlayerError = (uiErrorCode: string, subErrorCode: string, errorCode) => {
        const playbackUiError = new PlaybackError(uiErrorCode);
        playbackUiError.setSubCode(subErrorCode);
        const playbackError: LiveTvPlaybackError = new LiveTvPlaybackError(playbackUiError);
        Diagnostics.error(
            ShakaPlayerImpl.TAG,
            `Show Player Error {playbackError: `,
            playbackUiError,
            '}',
        );
        Log.e(ShakaPlayerImpl.TAG, `Show Player Error {playbackError: `, playbackUiError, '}');
        // TODO: ANIKUMAR - move this to uper layer.
        // liveTvPlaybackError.showVisualDisplay();
        QoEAppLogger.logPlaybackError(playbackError);
        Diagnostics.error(
            ShakaPlayerImpl.TAG,
            'Failed to start the playback {errorCode: ',
            errorCode,
            '}',
        );
        Log.e(ShakaPlayerImpl.TAG, 'Failed to start the playback {errorCode: ', errorCode, '}');
        this.setVideoPlayerState({
            state: VideoPlayerStates.FATAL_ERROR,
            error: { type: PlaybackErrorType.LiveTvPlaybackError, data: playbackError },
        });
    };

    private playVideoUrl(position?: number): boolean {
        this.clearPlayerErrorTimer();
        if (this.mVideoPlayer) {
            const playerMsoConfig = DeviceModel.getTiVoSettings().getPlayerMsoConfig();
            this.mVideoPlayer.configure(playerMsoConfig);
            this.mVideoPlayer
                .load(this.videoUrl)
                .then(() => {
                    // This runs if the asynchronous load is successful.
                    this.setVideoPlayerState({ state: VideoPlayerStates.READY });
                    Diagnostics.info(ShakaPlayerImpl.TAG, 'The video has now been loaded!');
                    Log.i(ShakaPlayerImpl.TAG, 'The video has now been loaded!');
                    this.fetchTextTrack();
                    Diagnostics.debug(
                        ShakaPlayerImpl.TAG,
                        'Get Text Tracks {subtitleTracks: ',
                        this.subtitleTracks,
                        '}',
                    );
                    Log.d(
                        ShakaPlayerImpl.TAG,
                        'Get Text Tracks {subtitleTracks: ',
                        this.subtitleTracks,
                        '}',
                    );
                    this.handleCaptionPreference();
                    this.setVideoPlayerState({ state: VideoPlayerStates.LOADED });
                    this.isFirstFrameTriggered = false;
                    if (position) {
                        this.seekTo(position);
                    }
                    QoEAppLogger.stopMediaHealthTimer();
                })
                .catch((error) => {
                    Diagnostics.error(ShakaPlayerImpl.TAG, 'Load Error {error: ', error, '}');
                    Log.e(ShakaPlayerImpl.TAG, 'Load Error {error: ', error, '}');
                    if (ShakaPlayerErrorMapper.isErrorHandlingRequired(error, this.videoUrl)) {
                        this.handlePlaybackError(error);
                    }
                });
        }
        return true;
    }

    /**
     * display subtitle.
     *
     * @param display if true, it will display subtitle/cc
     */
    displaySubtitles = (display: boolean) => {
        if (this.cc !== display) {
            Storage.getInstance(Storage.StorageType.SESSION_STORAGE).setItem(
                Storage.Keys.PLAYER_CC_STATUS,
                display,
            );
            this.cc = display;
        }
        this.mVideoPlayer.setTextTrackVisibility(display);
    };

    setWindowBounds = (bounds: number) => {
        if (bounds) throw new Error('Method not implemented.');
    };

    cleanUp = () => {
        clearTimeout(this.playbackRetryTimerId);
        this.playbackRetryTimerId = null;
        this.mAudioTracks = [];
        this.subtitleTracks = [];
    };

    stopPlayback = async () => {
        this.clearPlayerErrorTimer();
        this.isTrickPlayEnable = false;
        QoEAppLogger.updateAllHealthStatsMetrics(this.getPlayerStats());
        QoEAppLogger.updateProfileTime();
        this.isFirstFrameTriggered = false;
        QoEAppLogger.stopMediaHealthTimer();
        this.unMute();
        this.unPause();
        this.setSpeed(SPEED.SPEED_NORMAL);
        this.cleanUp();
        await this.unloadPlayer();
        Log.i(ShakaPlayerImpl.TAG, 'stopPlayback');
    };

    private unloadPlayer = (): Promise<void> => {
        return this.mVideoPlayer
            .unload()
            .then(() => {
                Log.i(ShakaPlayerImpl.TAG, `PlayerEvent Player unloaded successfully.}`);
                this.setVideoPlayerState({ state: VideoPlayerStates.UNLOADED });
            })
            .catch((error) => {
                Log.i(ShakaPlayerImpl.TAG, 'PlayerEvent Error unloading player:', error);
            });
    };

    private onPlayingHandler = (event) => {
        Diagnostics.info(ShakaPlayerImpl.TAG, '{PlayerEvent: playing}');
        Log.i(ShakaPlayerImpl.TAG, '{PlayerEvent: playing}');
        QoEAppLogger.updateHealthStatsMetricsByEvent({
            type: QoEAppLogger.PlayerEvents.PLAY,
            timeStamp: event.timeStamp,
        });
        this.setVideoPlayerState({ state: VideoPlayerStates.PLAYING });
        this.errorTime = null;
        this.clearPlayerErrorTimer();
        if (!this.isFirstFrameTriggered) {
            this.retryCount = 0;
            QoEAppLogger.logFirstFrameEvent();
            this.isFirstFrameTriggered = true;
            this.setVideoPlayerState({ state: VideoPlayerStates.FIRST_FRAME });
            VuLogger.logTrickPlayEvent(VuLogger.TrickplayMode.PLAY, this.getCurrentTimeInMs());
            Diagnostics.info(ShakaPlayerImpl.TAG, 'FirstFrameTriggered');
            // log mediaHealth event and schedule event for every 5 minutes
            QoEAppLogger.startMediaHealthTimer();
        }

        // condition to logStreamingRecovery only after error recovery
        if (VuLogger.streamingRecoveryFlag) {
            VuLogger.logStreamingRecovery(VuLogger.StreamingRecoveryType.PLAYBACK_ERROR);
            VuLogger.streamingRecoveryFlag = false;
        }
    };

    private onPauseHandler = (event) => {
        this.setVideoPlayerState({ state: VideoPlayerStates.PAUSE });
        Diagnostics.info(ShakaPlayerImpl.TAG, '{PlayerEvent: pause}');
        Log.i(ShakaPlayerImpl.TAG, '{PlayerEvent: pause}');
        QoEAppLogger.updateHealthStatsMetricsByEvent({
            type: QoEAppLogger.PlayerEvents.PAUSE,
            timeStamp: event.timeStamp,
        });
    };

    private onStalledHandler = (event) => {
        Diagnostics.info(ShakaPlayerImpl.TAG, 'PlayerEvent: stalled');
        Log.i(ShakaPlayerImpl.TAG, 'PlayerEvent: stalled');
        QoEAppLogger.updateHealthStatsMetricsByEvent({
            type: QoEAppLogger.PlayerEvents.STALLED,
            timeStamp: event.timeStamp,
        });
    };

    private onTimeUpdateHandler = () => {
        if (this.isEndOfStream()) {
            Log.i(ShakaPlayerImpl.TAG, 'PlayerEvent: TimeUpdate EndOfStream');
            this.setVideoPlayerState({ state: VideoPlayerStates.END_OF_STREAM });
        }
    };

    private onTracksChangedHandler = () => {
        Diagnostics.info(ShakaPlayerImpl.TAG, '{PlayerEvent: trackschanged}');
        Log.i(ShakaPlayerImpl.TAG, '{PlayerEvent: trackschanged}');
    };

    private onErrorHandler = (error: any) => {
        Diagnostics.error(ShakaPlayerImpl.TAG, 'PlayerEvent: error', error.detail);
        Log.e(ShakaPlayerImpl.TAG, 'PlayerEvent: error', error.detail);
        if (ShakaPlayerErrorMapper.isErrorHandlingRequired(error.detail, this.videoUrl)) {
            this.handlePlaybackError(error.detail);
        }
    };

    private onBufferingHandler = (state: any) => {
        if (state.buffering === true) {
            Diagnostics.info(ShakaPlayerImpl.TAG, '{PlayerEvent: buffering}');
            Log.i(ShakaPlayerImpl.TAG, '{PlayerEvent: buffering}');
            if (!this.isTrickPlayEnable) {
                this.setVideoPlayerState({ state: VideoPlayerStates.BUFFERING });
                if (this.isFirstFrameTriggered === true) {
                    QoEAppLogger.updateHealthStatsMetricsByEvent({
                        type: QoEAppLogger.PlayerEvents.STALLED,
                        timeStamp: state.timeStamp,
                    });
                }
            }
        } else {
            // consider this as playing since buffering property is false
            QoEAppLogger.updateHealthStatsMetricsByEvent({
                type: QoEAppLogger.PlayerEvents.PLAY,
                timeStamp: state.timeStamp,
            });
        }
    };

    private onEmsgHandler = () => {
        Diagnostics.info(ShakaPlayerImpl.TAG, '{PlayerEvent: emsg}');
        Log.i(ShakaPlayerImpl.TAG, '{PlayerEvent: emsg}');
    };

    private onUnloadingHandler = () => {
        Diagnostics.info(ShakaPlayerImpl.TAG, '{PlayerEvent: unloading}');
        Log.i(ShakaPlayerImpl.TAG, '{PlayerEvent: unloading}');
        this.setVideoPlayerState({ state: VideoPlayerStates.UNLOADED });
    };

    private onStreamingHandler = () => {
        Diagnostics.info(ShakaPlayerImpl.TAG, '{PlayerEvent: streaming}');
        Log.i(ShakaPlayerImpl.TAG, '{PlayerEvent: streaming}');
    };

    private onStateChangeHandler = (event: any) => {
        // TODO: More states can be added here.
        Diagnostics.info(ShakaPlayerImpl.TAG, `{PlayerEvent: onstatechange - ${event.state}}`);
        Log.i(ShakaPlayerImpl.TAG, `{PlayerEvent: onstatechange - ${event.state}}`);
    };

    private onStateIdleHandler = () => {
        Diagnostics.info(ShakaPlayerImpl.TAG, '{PlayerEvent: onstateidle}');
        Log.i(ShakaPlayerImpl.TAG, '{PlayerEvent: onstateidle}');
    };

    private onRateChangeHanlder = (state: any) => {
        Diagnostics.info(ShakaPlayerImpl.TAG, '{PlayerEvent: ratechange}');
        Log.i(ShakaPlayerImpl.TAG, '{PlayerEvent: ratechange}');
        if (!state.buffering) {
            this.setVideoPlayerState({ state: VideoPlayerStates.RATE_CHANGE });
            this.errorTime = null;
            this.clearPlayerErrorTimer();
        }
    };

    private onMetaDataHandler = () => {
        Diagnostics.info(ShakaPlayerImpl.TAG, '{PlayerEvent: metadata found}');
        Log.i(ShakaPlayerImpl.TAG, '{PlayerEvent: metadata found}');
    };

    private onManifestParsedHandler = (event) => {
        Diagnostics.info(ShakaPlayerImpl.TAG, '{PlayerEvent: manifestparsed}');
        Log.i(ShakaPlayerImpl.TAG, '{PlayerEvent: manifestparsed}');
        QoEAppLogger.updateHealthStatsMetricsByEvent({
            type: QoEAppLogger.PlayerEvents.BUFFERING,
            timeStamp: event.timeStamp,
        });
    };

    private onTextChangedHandler = () => {
        this.fetchTextTrack();
    };

    private onAdaptationHandler = (event) => {
        const oldTrack = event?.Af;
        Diagnostics.info(ShakaPlayerImpl.TAG, '{PlayerEvent: adaptation}');
        Log.i(ShakaPlayerImpl.TAG, '{PlayerEvent: adaptation}');
        if (!QoEAppLogger.profiles.length) {
            QoEAppLogger.createProfiles(this.getVariants());
        }
        if (!QoEAppLogger.isAdaptationEventFirstTime && !oldTrack) {
            QoEAppLogger.isAdaptationEventFirstTime = true;
            const time = Date.now() - QoEAppLogger.watchContentStartTime;
            QoEAppLogger.updateProfileTime(this.getPlayerStats(), time);
        } else if (oldTrack) {
            const intervalTime =
                QoEAppLogger.mediaHealthStartIntervalTime || QoEAppLogger.watchContentStartTime;
            const time = Date.now() - intervalTime;
            QoEAppLogger.updateProfileTime(event.Af, time);
            QoEAppLogger.mediaStats.profileShiftCount += 1;
            QoEAppLogger.mediaHealthStartIntervalTime = Date.now();
        }
    };

    private removeEventListners = () => {
        if (this.mMediaElement) {
            this.mMediaElement!.removeEventListener('playing', this.onPlayingHandler);
            this.mMediaElement!.removeEventListener('pause', this.onPauseHandler);
            this.mMediaElement!.removeEventListener('stalled', this.onStalledHandler);
            this.mMediaElement!.removeEventListener('timeupdate', this.onTimeUpdateHandler);
        }
        this.mVideoPlayer.removeEventListener('trackschanged', this.onTracksChangedHandler);
        this.mVideoPlayer.removeEventListener('error', this.onErrorHandler);
        this.mVideoPlayer.removeEventListener('buffering', this.onBufferingHandler);
        this.mVideoPlayer.removeEventListener('emsg', this.onEmsgHandler);
        this.mVideoPlayer.removeEventListener('unloading', this.onUnloadingHandler);
        this.mVideoPlayer.removeEventListener('streaming', this.onStreamingHandler);
        this.mVideoPlayer.removeEventListener('onstatechange', this.onStateChangeHandler);
        this.mVideoPlayer.removeEventListener('onstateidle', this.onStateIdleHandler);
        this.mVideoPlayer.removeEventListener('ratechange', this.onRateChangeHanlder);
        this.mVideoPlayer.removeEventListener('metadata', this.onMetaDataHandler);
        this.mVideoPlayer.removeEventListener('manifestparsed', this.onManifestParsedHandler);
        this.mVideoPlayer.removeEventListener('textchanged', this.onTextChangedHandler);
        this.mVideoPlayer.removeEventListener('adaptation', this.onAdaptationHandler);
    };

    registerEventListeners = () => {
        if (this.mMediaElement) {
            this.mMediaElement!.addEventListener('playing', this.onPlayingHandler);
            this.mMediaElement!.addEventListener('pause', this.onPauseHandler);
            this.mMediaElement!.addEventListener('stalled', this.onStalledHandler);
            this.mMediaElement!.addEventListener('timeupdate', this.onTimeUpdateHandler);
        }
        this.mVideoPlayer.addEventListener('trackschanged', this.onTracksChangedHandler);
        this.mVideoPlayer.addEventListener('error', this.onErrorHandler);
        this.mVideoPlayer.addEventListener('buffering', this.onBufferingHandler);
        this.mVideoPlayer.addEventListener('emsg', this.onEmsgHandler);
        this.mVideoPlayer.addEventListener('unloading', this.onUnloadingHandler);
        this.mVideoPlayer.addEventListener('streaming', this.onStreamingHandler);
        this.mVideoPlayer.addEventListener('onstatechange', this.onStateChangeHandler);
        this.mVideoPlayer.addEventListener('onstateidle', this.onStateIdleHandler);
        this.mVideoPlayer.addEventListener('ratechange', this.onRateChangeHanlder);
        this.mVideoPlayer.addEventListener('metadata', this.onMetaDataHandler);
        this.mVideoPlayer.addEventListener('manifestparsed', this.onManifestParsedHandler);
        this.mVideoPlayer.addEventListener('textchanged', this.onTextChangedHandler);
        this.mVideoPlayer.addEventListener('adaptation', this.onAdaptationHandler);
    };
}
